import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Upload, Trash2, Send, Clock, CheckCircle, XCircle } from "lucide-react";
import { ShowcaseHeroBackground } from "@/components/ShowcaseHeroBackground";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending: { label: "Pending Review", icon: Clock, color: "text-yellow-400", bg: "bg-yellow-400/10", border: "border-yellow-400/30" },
  approved: { label: "Approved", icon: CheckCircle, color: "text-[#39FF14]", bg: "bg-[#39FF14]/10", border: "border-[#39FF14]/30" },
  denied: { label: "Denied", icon: XCircle, color: "text-red-400", bg: "bg-red-400/10", border: "border-red-400/30" },
};

export default function DesignRequestPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [description, setDescription] = useState("");
  const [imageUrls, setImageUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [myRequests, setMyRequests] = useState([]);
  const fileInputRef = useRef(null);

  const fetchMine = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/api/design-requests/mine`);
      setMyRequests(data);
    } catch (e) { /* silent */ }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=/design-request"); return; }
    fetchMine();
  }, [user, authLoading, navigate, fetchMine]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    if (imageUrls.length + files.length > 15) {
      setError("Max 15 images per request.");
      return;
    }
    setUploading(true);
    setError("");
    const newUrls = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/api/design-requests/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        newUrls.push(`${API}/api/files/${data.path}`);
      } catch (e) {
        setError("An image failed to upload. Others were saved.");
      }
    }
    setImageUrls([...imageUrls, ...newUrls]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (url) => setImageUrls(imageUrls.filter(u => u !== url));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (description.trim().length < 10) { setError("Please describe your design in at least 10 characters."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/design-requests`, { description, image_urls: imageUrls });
      setFeedback("Request submitted! We'll review it and email you.");
      setDescription("");
      setImageUrls([]);
      await fetchMine();
      setTimeout(() => setFeedback(""), 4000);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to submit — please try again.");
    } finally { setSubmitting(false); }
  };

  if (authLoading || !user) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}><div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" /></div>;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      {/* Top hero band — greyed-out showcase photo backdrop behind the intro. */}
      <div className="relative overflow-hidden border-b border-[#1A1A1A]">
        <ShowcaseHeroBackground intervalMs={9000} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <Link to="/" className="flex items-center text-[#A1A1A1] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider mb-6" data-testid="design-back">
            <ArrowLeft className="h-3 w-3 mr-2" /> Back home
          </Link>

          <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-3">Custom Build Request</p>
          <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2] leading-[0.95] mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
            Design A<br /><span className="text-[#39FF14]">Custom Xbox</span>
          </h1>
          <p className="text-[#A1A1A1] text-sm sm:text-base max-w-2xl">
            Tell us what you want built. Describe the look, colors, mods, and any specific parts.
            Add reference photos — we&apos;ll review your idea and get back to you within a couple of business days.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {feedback && <div className="mb-6 p-4 border border-[#39FF14]/30 bg-[#39FF14]/5 text-[#39FF14] text-sm font-mono" data-testid="design-feedback">{feedback}</div>}
        {error && <div className="mb-6 p-4 border border-red-500/30 bg-red-500/10 text-red-400 text-sm font-mono" data-testid="design-error">{error}</div>}

        <form onSubmit={submit} className="space-y-6" data-testid="design-form">
          <div>
            <label className="block font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-2">Describe Your Design</label>
            <textarea
              data-testid="design-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={8}
              maxLength={4000}
              placeholder="e.g. Modded original Xbox with a translucent purple shell, RGB LED fan, 2TB HDD, chrome vents, and a custom logo etched on the top. Aiming for a retrofuturistic look…"
              className="w-full bg-[#0D0D0D] border border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:outline-none focus:ring-1 focus:ring-[#39FF14] px-4 py-3 text-sm font-mono resize-none"
              required
            />
            <p className="font-mono text-[10px] text-[#A1A1A1]/60 mt-1 text-right">{description.length}/4000</p>
          </div>

          <div>
            <label className="block font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-2">Reference Photos (optional)</label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {imageUrls.map((url, idx) => (
                <div key={idx} className="relative aspect-square border border-[#1A1A1A] bg-[#0D0D0D] group" data-testid={`design-image-${idx}`}>
                  <img src={url} alt="reference" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(url)} className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity" data-testid={`design-remove-image-${idx}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {imageUrls.length < 15 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="aspect-square border border-dashed border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] flex flex-col items-center justify-center transition-colors disabled:opacity-50"
                  data-testid="design-upload-btn"
                >
                  <Upload className="h-5 w-5 mb-1" />
                  <span className="font-mono text-[9px] uppercase tracking-wider">{uploading ? "…" : "Add"}</span>
                </button>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={(e) => handleUpload(Array.from(e.target.files))} className="hidden" data-testid="design-file-input" />
            <p className="font-mono text-[10px] text-[#A1A1A1]/60 mt-1">{imageUrls.length}/15 images</p>
          </div>

          <Button type="submit" disabled={submitting || uploading} data-testid="design-submit" className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6 disabled:opacity-50">
            <Send className="h-3 w-3 mr-2" />
            {submitting ? "Submitting…" : "Submit For Approval"}
          </Button>
        </form>

        {myRequests.length > 0 && (
          <div className="mt-16" data-testid="my-requests-section">
            <h2 className="text-xl font-black uppercase tracking-tighter text-[#F2F2F2] mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Your Requests</h2>
            <div className="space-y-3">
              {myRequests.map((r) => {
                const s = STATUS_META[r.status] || STATUS_META.pending;
                const Icon = s.icon;
                return (
                  <div key={r.id} className={`bg-[#0D0D0D] border ${s.border} p-4`} data-testid={`my-request-${r.id}`}>
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1">
                        <p className="font-mono text-[10px] text-[#A1A1A1] uppercase tracking-wider">#{r.id.slice(-8)} · {new Date(r.created_at).toLocaleDateString()}</p>
                        <p className="text-[#F2F2F2] text-sm mt-1 line-clamp-2">{r.description}</p>
                        {r.admin_note && (
                          <div className={`mt-2 p-2 ${s.bg} border ${s.border}`}>
                            <p className={`font-mono text-[10px] uppercase tracking-wider ${s.color} mb-1`}>{r.status === "approved" ? "Note" : "Reason"}</p>
                            <p className="text-[#F2F2F2] text-xs">{r.admin_note}</p>
                          </div>
                        )}
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 ${s.bg} ${s.color} font-mono text-[10px] uppercase tracking-wider shrink-0`}>
                        <Icon className="h-3 w-3" />
                        {s.label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
